import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { filter, map, take, timeout, of } from 'rxjs';
import { environment } from '@environments/environment';
import { AppService } from '@Services/app.service';
import { UsermenuService } from '@User/usermenu/usermenu-extras/usermenu.service';
import { UserMenuStore } from 'projects/evolutics-shared-lib/src/lib/@ngrx/usermenu/usermenu.reducer';
import { PermissionManager } from 'projects/evolutics-shared-lib/src/lib/functions/permission-manager.class';
import {
  indexPermissionMenus,
  isWritePermissionRoute,
  resolvePermissionMenu,
} from 'projects/evolutics-shared-lib/src/lib/functions/menu-permission';

@Injectable({ providedIn: 'root' })
export class IsAllowedGuard {
  constructor(
    public appS: AppService,
    public router: Router,
    public userMS: UsermenuService,
  ) {}

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.canActivate(route, state);
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    if (environment.noUserRoleCheck || route.firstChild) return true;
    // Recheck modes such as ?action=edit when Angular reuses the current page.
    if (route.routeConfig) route.routeConfig.runGuardsAndResolvers ??= 'paramsOrQueryParamsChange';
    const check = () => {
      const chain = route.pathFromRoot;
      const ids = chain.map((item) => item.routeConfig?.customData?.miID).filter(Boolean);
      const menu = resolvePermissionMenu(
        indexPermissionMenus(this.appS.topMenu),
        state.url,
        this.appS.system,
        ids,
      );
      const permission = menu && PermissionManager.selectFromComplexMap(menu.id, menu.parentID, menu.system);
      const operation = [...chain].reverse().find((item) => item.data?.permissionOperation)
        ?.data.permissionOperation;
      const write = isWritePermissionRoute(
        state.url,
        chain.map((item) => item.routeConfig?.path || ''),
        operation,
      );
      const allowed = permission?.viewAccess === true && (!write || permission.editAccess === true);
      if (!allowed) {
        PermissionManager.pagePermission = undefined;
        return this.router.parseUrl('/no-access');
      }
      PermissionManager.pagePermission = {
        ...permission,
        slug: menu.id,
        parentSlug: menu.parentID,
        system: menu.system,
      };
      PermissionManager.lastRouteMID = menu.id;
      PermissionManager.lastParentRouteMID = menu.parentID;
      return true;
    };
    if (PermissionManager.bUsermenuConfigMap) return check();
    // Authentication can finish before cached/online menu maps arrive.
    return this.userMS.store
      .select(UserMenuStore.selectors.permissionMapsReady)
      .pipe(
        filter(Boolean),
        take(1),
        map(check),
        timeout({ first: 15000, with: () => of(this.router.parseUrl('/no-access')) }),
      );
  }
}
